/**
 * BEKRA compact header dropdowns — small enhancement on top of Horizon's
 * <header-menu> (assets/header-menu.js), which stays the only menu engine:
 * it opens on hover/focus, keeps a single active item and sets aria-expanded.
 *
 * Added here (desktop only, header-menu.header-menu--bekra-compact):
 * - each dropdown is positioned under its own menu item (the menu bar clips
 *   overflow, so the panel can't be anchored to the <li> via CSS),
 * - click / tap / Enter: opens a closed dropdown; "#" parents toggle with
 *   Enter or a second tap (a mouse click keeps the hovered dropdown open);
 *   parents with a real URL navigate once their dropdown is open
 *   (touch: first tap opens, second tap follows the link),
 * - Esc and clicks outside close the open dropdown.
 */
const GUTTER = 16;

class BekraHeaderDropdowns {
  /** @param {HTMLElement} menu */
  constructor(menu) {
    this.menu = menu;
    /** @type {HTMLElement | null} */
    this.armedLink = null;
    this.lastPointerType = 'mouse';
    /** @type {HTMLElement | null} */
    this.closedLink = null;

    this.position = this.position.bind(this);
    window.addEventListener('resize', () => requestAnimationFrame(this.position), { passive: true });
    menu.addEventListener('pointerenter', (event) => this.#onEnter(event), true);
    menu.addEventListener('focusin', (event) => this.#onFocusIn(event));
    menu.addEventListener('pointerdown', (event) => (this.lastPointerType = event.pointerType || 'mouse'), true);
    menu.addEventListener('click', (event) => this.#onClick(event));
    document.addEventListener('keydown', (event) => this.#onKeydown(event));
    document.addEventListener('pointerdown', (event) => this.#onOutside(event), true);
    this.position();
  }

  /** Top-level links that own a dropdown (overflow "Mehr" items excluded). */
  get parentLinks() {
    return /** @type {HTMLElement[]} */ ([
      ...this.menu.querySelectorAll('.menu-list__list-item:not([slot="overflow"]) > .menu-list__link[aria-haspopup]'),
    ]);
  }

  get openLink() {
    return this.parentLinks.find((link) => link.getAttribute('aria-expanded') === 'true') || null;
  }

  /** Place every dropdown under its own item, kept inside the viewport. */
  position() {
    for (const link of this.parentLinks) {
      const item = link.parentElement;
      const panel = /** @type {HTMLElement | null} */ (item?.querySelector(':scope > .menu-list__submenu--bekra-dropdown'));
      const container = panel?.offsetParent;
      if (!item || !panel || !container) continue;

      const containerLeft = container.getBoundingClientRect().left;
      const itemLeft = item.getBoundingClientRect().left;
      const maxLeft = window.innerWidth - GUTTER - panel.offsetWidth;
      const left = Math.max(GUTTER, Math.min(itemLeft - 12, maxLeft)) - containerLeft;
      panel.style.setProperty('--bekra-dropdown-left', `${Math.round(left)}px`);
    }
  }

  /** @param {HTMLElement} link */
  #open(link) {
    this.menu.removeAttribute('data-bekra-closed');
    this.closedLink = null;
    this.position();
    // Horizon's own activation (same path as hover/focus).
    /** @type {any} */ (this.menu).activate?.({ target: link.parentElement });
    // Re-opening the item Horizon still holds as active (its close delay was
    // cut short by Esc / outside click) is a no-op there: restore the state.
    setTimeout(() => {
      const otherOpen = this.parentLinks.some((other) => other !== link && other.getAttribute('aria-expanded') === 'true');
      if (!otherOpen && !this.menu.hasAttribute('data-bekra-closed')) link.setAttribute('aria-expanded', 'true');
    }, 30);
  }

  /** @param {HTMLElement} link */
  #close(link) {
    this.armedLink = null;
    this.closedLink = link;
    // Hide at once; Horizon resets its state after its normal close delay.
    this.menu.setAttribute('data-bekra-closed', '');
    /** @type {any} */ (this.menu).deactivate?.({ target: link.parentElement });
    link.setAttribute('aria-expanded', 'false');
  }

  /** @param {PointerEvent} event */
  #onEnter(event) {
    if (event.pointerType !== 'mouse') return;
    // Only when the pointer enters a top-level item from outside, not when it
    // moves inside an item (Esc must keep it closed until the item is re-entered).
    const item = /** @type {Element} */ (event.target);
    if (!item.matches?.('.menu-list__list-item')) return;
    const link = item.querySelector(':scope > .menu-list__link[aria-haspopup]');
    if (link instanceof HTMLElement && link === this.closedLink) return this.#open(link);
    this.menu.removeAttribute('data-bekra-closed');
    this.position();
  }

  /** @param {FocusEvent} event */
  #onFocusIn(event) {
    const link = /** @type {Element} */ (event.target).closest?.('.menu-list__list-item')?.querySelector(':scope > .menu-list__link');
    if (link && link === this.closedLink) return; // focus returned by Esc: stay closed
    this.menu.removeAttribute('data-bekra-closed');
    this.closedLink = null;
    this.position();
  }

  /** @param {MouseEvent} event */
  #onClick(event) {
    const link = /** @type {Element} */ (event.target).closest?.('.menu-list__link[aria-haspopup]');
    if (!(link instanceof HTMLElement) || !this.parentLinks.includes(link)) return;

    const isOpen = link.getAttribute('aria-expanded') === 'true' && !this.menu.hasAttribute('data-bekra-closed');
    const isPlaceholder = (link.getAttribute('href') || '#') === '#';
    const isKeyboard = event.detail === 0;
    const isTouch = !isKeyboard && (this.lastPointerType === 'touch' || this.lastPointerType === 'pen');

    if (isPlaceholder) {
      event.preventDefault();
      // Mouse: hover already opened it, a click keeps it open.
      // Keyboard (Enter) and a second tap toggle it closed.
      if (isOpen && (isKeyboard || (isTouch && this.armedLink === link))) this.#close(link);
      else {
        this.#open(link);
        this.armedLink = link;
      }
      return;
    }

    // Real URL: follow it only once the dropdown is open (touch: second tap).
    if (!isOpen || (isTouch && this.armedLink !== link)) {
      event.preventDefault();
      this.#open(link);
      this.armedLink = link;
    }
  }

  /** @param {KeyboardEvent} event */
  #onKeydown(event) {
    if (event.key !== 'Escape') return;
    const link = this.openLink;
    if (!link || this.menu.hasAttribute('data-bekra-closed')) return;
    if (this.menu.contains(document.activeElement)) link.focus();
    this.#close(link);
  }

  /** @param {PointerEvent} event */
  #onOutside(event) {
    const link = this.openLink;
    if (!link || this.menu.contains(/** @type {Node} */ (event.target))) return;
    this.#close(link);
  }
}

const init = () => {
  document.querySelectorAll('header-menu.header-menu--bekra-compact').forEach((menu) => {
    if (menu instanceof HTMLElement && !menu.dataset.bekraDropdowns) {
      menu.dataset.bekraDropdowns = '';
      new BekraHeaderDropdowns(menu);
    }
  });
};

customElements.whenDefined('header-menu').then(init);
document.addEventListener('shopify:section:load', () => customElements.whenDefined('header-menu').then(init));
